import React from 'react';

import { Flex, Text, Button } from '@opendoor/bricks/core';

import { CloseDatePicker } from 'sellDirect/components/closingDash/contract/CloseDatePicker';

import { IResetableFormRenderProps } from 'sellDirect/components/closingDash/ResetableForm';
import { LocalDate } from '@js-joda/core';
import { FULL_DATE_AND_DAY_OF_WEEK_FORMAT } from 'shared/helpers/datetime';
import { CloseDateFormState } from 'sellDirect/components/closingDash/changeCloseDate/CloseDateForm';

const NEXT_BUTTON_MAX_WIDTH = 327;

interface CloseDateFormContentProps {
  shouldSeeAgentExperience: boolean;
  currentCloseEscrowDate: LocalDate;
  changeCloseEscrowDateDeadline: LocalDate;
  availableCloseDates: LocalDate[];
  loading: boolean;
  resetableForm: IResetableFormRenderProps<CloseDateFormState>;
  onCancel: () => void;
  canCancel: boolean;
}

/**
 * Form content for the form that handles requesting a change for the close of escrow date
 *
 * @param currentCloseEscrowDate - Current close of escrow date
 * @param changeCloseEscrowDateDeadline - The deadline for requesting a new close date
 * @param availableCloseDates - Available dates the user can chose from
 * @param loading - Whether we're currently loading data (i.e. submitting)
 * @param resetableForm - The resetableForm object
 * @param onCancel - Callback to call when the user clicks the cancel button (goes back to summary view)
 * @param canCancel - Wether or not the user can cancel/close this form
 *
 * component-test-check-ignore storybook-check-ignore
 */
export const CloseDateFormContent: React.FC<CloseDateFormContentProps> = ({
  shouldSeeAgentExperience,
  currentCloseEscrowDate,
  changeCloseEscrowDateDeadline,
  availableCloseDates,
  loading,
  resetableForm,
  onCancel,
  canCancel,
}) => {
  const { formState, onSubmit, onUpdate } = resetableForm;

  const formStateCloseEscrowDate = formState.closeEscrowDate
    ? LocalDate.parse(formState.closeEscrowDate)
    : undefined;

  // Disable save button if the selected escrow date is the same as the existing escrow date
  const disabled = formStateCloseEscrowDate
    ? currentCloseEscrowDate.equals(formStateCloseEscrowDate)
    : false;

  return (
    <Flex flexDir="column" gridGap="9">
      <CloseDatePicker
        shouldSeeAgentExperience={shouldSeeAgentExperience}
        availableCloseDates={availableCloseDates}
        closeDate={formStateCloseEscrowDate}
        onUpdate={(date) => onUpdate('closeEscrowDate', date.toString())}
        disabled={loading}
      />

      <Text>
        This will be locked after{' '}
        {changeCloseEscrowDateDeadline.format(FULL_DATE_AND_DAY_OF_WEEK_FORMAT)}.
      </Text>

      <Flex gridGap="4">
        <Button
          analyticsName="cake-submit-change-coe-request"
          onClick={onSubmit}
          disabled={disabled || loading}
          loading={loading}
          maxWidth={NEXT_BUTTON_MAX_WIDTH}
          aria-label="Save and submit close of escrow date change request"
        >
          Save
        </Button>
        {canCancel && (
          <Button
            analyticsName="cake-cancel-change-coe-request"
            disabled={loading}
            onClick={onCancel}
            variant="secondary"
            maxWidth={NEXT_BUTTON_MAX_WIDTH}
            aria-label="Cancel close of escrow date change request"
          >
            Cancel
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
